import { useMemo } from "react";
import { useNavigate } from "react-router-dom";

import {
  DetailsRow,
  IColumn,
  ISelection,
  Link,
  ScrollablePane,
  SelectionMode,
  ShimmeredDetailsList,
  Stack,
  Text
} from "@bps/fluent-ui";
import { CopyableGuid } from "@components/CopyableText";
import { ScrollListener } from "@components/tables/ScrollListener";
import { Tenant } from "@libs/api/gateways/plt/plt-gateway.dtos";

import { SiteAgentVersionCell } from "./SiteAgentVersionCell";

interface Props {
  items: Tenant[];
  selection: ISelection;
  enableShimmer?: boolean;
  errorMessage?: string;
  hasNextPage: boolean;
  onScrolledToBottom: () => Promise<void>;
}

export const DeployManagementTable: React.FC<Props> = ({
  items,
  selection,
  enableShimmer,
  errorMessage,
  hasNextPage,
  onScrolledToBottom
}) => {
  const navigate = useNavigate();

  const columns: IColumn[] = useMemo(
    () => [
      {
        key: "tenant",
        name: "Tenant",
        minWidth: 220,
        maxWidth: 320,
        isResizable: true,
        onRender: (tenant: Tenant) => (
          <Stack>
            <Link onClick={() => navigate(`/tenants/${tenant.id}/plt-details`)}>
              {tenant.name}
            </Link>
            <CopyableGuid value={tenant.id} variant="small" />
          </Stack>
        )
      },
      {
        key: "fieldDevice",
        name: "Field Device",
        minWidth: 260,
        maxWidth: 320,
        isResizable: true,
        onRender: (tenant: Tenant) =>
          tenant.siteAuthorityFieldDeviceId ? (
            <Link
              href={`/field-mgmt/${tenant.siteAuthorityFieldDeviceId}/fd-overview`}
              target="_blank"
            >
              {tenant.siteAuthorityFieldDeviceId}
            </Link>
          ) : (
            <Text variant="small">No field device</Text>
          )
      },
      {
        key: "siteAgent",
        name: "Site Agent",
        minWidth: 160,
        maxWidth: 220,
        isResizable: true,
        onRender: (tenant: Tenant) =>
          tenant.siteAuthorityFieldDeviceId ? (
            <SiteAgentVersionCell
              fieldDeviceId={tenant.siteAuthorityFieldDeviceId}
            />
          ) : (
            <Text variant="small">No field device</Text>
          )
      }
    ],
    [navigate]
  );

  return (
    <Stack
      verticalFill
      styles={{
        root: {
          position: "relative",
          height: "100%",
          flex: "1 1 0",
          overflowX: "auto"
        }
      }}
    >
      <ScrollablePane>
        <ShimmeredDetailsList
          enableShimmer={enableShimmer}
          errorMessage={errorMessage}
          stickyHeader
          items={items}
          columns={columns}
          setKey="deploy-management-tenants"
          shimmerLines={5}
          onShouldVirtualize={() => true}
          selectionMode={SelectionMode.multiple}
          selection={selection}
          selectionPreservedOnEmptyClick
          onRenderRow={row =>
            row ? (
              <DetailsRow
                {...row}
                styles={{
                  cell: { userSelect: "text" }
                }}
              />
            ) : null
          }
        />
        <ScrollListener
          hasNextPage={hasNextPage}
          onScrolledToBottom={onScrolledToBottom}
        />
      </ScrollablePane>
    </Stack>
  );
};
